import './PayloadConfigModal.scss'
import { useMemo } from 'react'
import {
    FooterAction,
    HeaderAction,
    HeaderActionsContainer,
    HeaderTitle,
    HeaderTitleContainer,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from '../../../../../CommonComponents/modals/Modal'
import useModal from '../../../hooks/useModal'
import useForm from '../../../hooks/useForm'
import { CloseSvg } from '../../../../../CommonComponents/svgs/Svgs'
import { RemoteAppDataType } from '../../../../types'
import { isIPValid, onKeyDown, preventKeyInputs, showToastMessage, validateNumberWithoutDecimal } from '../../../../../../utils/commonFunctions'

const PayloadConfigModal = () => {

    const { setFormData } = useForm()
    const { modals, setModals } = useModal()
    const open = useMemo(() => modals?.configured?.name === 'payloadAppConfig', [modals?.configured?.name])
    const selectedPayload: RemoteAppDataType = useMemo(() => modals?.configured?.data, [modals?.configured?.data])
    const Interfaces = useMemo(() => selectedPayload && selectedPayload?.interfaces || [], [selectedPayload])
    const disabled = useMemo(() => Interfaces?.length === 0 || (Interfaces.includes('CAN') && selectedPayload?.canPath0 === '') || (Interfaces.includes('Ethernet') && !isIPValid(selectedPayload?.payloadDeviceIP ?? '')), [selectedPayload, Interfaces])

    const onFormChange = (data: RemoteAppDataType) => {
        setModals((prev) => {
            return {
                ...prev!,
                configured: {
                    ...prev?.configured!,
                    name: 'payloadAppConfig',
                    data
                }
            }
        })
    }

    const onClose = () => {
        setModals((prev) => {
            return {
                ...prev,
                configured: null
            }
        })
    }

    const onSave = () => {
        setFormData(prev => {
            const filterApp = prev?.hwil?.remoteAppConfiguration?.remoteApp?.AppData.filter(app => app?.id !== selectedPayload?.id)
            return {
                ...prev,
                hwil: {
                    ...prev.hwil,
                    remoteAppConfiguration: {
                        ...prev.hwil.remoteAppConfiguration,
                        remoteApp: {
                            ...prev.hwil.remoteAppConfiguration.remoteApp,
                            AppData: [...filterApp, selectedPayload]
                        }
                    }
                }
            }
        })
        onClose()
    }

    const onPinChange = (port: number, pin: number, value: string) => {
        const newConfigurations = { ...selectedPayload }
        if (Number(value) < 0 || Number(value) > 7) return;
        else if (newConfigurations.gpio && value === '') {
            newConfigurations.gpio[port]['pin'][pin] = value;
            onFormChange(newConfigurations);
        } else if (newConfigurations?.gpio && (!newConfigurations.gpio[port]['pin'].includes(value) && newConfigurations.gpio[port].interrupt !== value) && value?.length <= 1) {
            newConfigurations.gpio[port]['pin'][pin] = value;
            onFormChange(newConfigurations);
        } else if (newConfigurations?.gpio && (newConfigurations?.gpio[port]['pin'].includes(value) || newConfigurations.gpio[port].interrupt === value)) {
            showToastMessage('info', `${value} already used for this Port`);
        }
    }

    const onPortChange = (port: number, value: string) => {
        const newConfigurations = { ...selectedPayload }
        if (Number(value) < 0 || Number(value) > 7) return;
        else if (newConfigurations.gpio && value === '') {
            newConfigurations.gpio[port]['interrupt'] = value
            onFormChange(newConfigurations)
        }
        else if (newConfigurations?.gpio && !newConfigurations.gpio[port]['pin'].includes(value) && value?.length <= 1) {
            newConfigurations.gpio[port]['interrupt'] = value
            onFormChange(newConfigurations)
        } else if (newConfigurations?.gpio && newConfigurations.gpio[port]['pin'].includes(value)) {
            showToastMessage('info', `${value} already used for this Port`);
        }
    }

    if (!open) return null
    return (
        <Modal open={open} pinned={true} disabled={true} className={`scenario__payload__configuration__modal`}>
            <ModalHeader>
                <HeaderTitleContainer>
                    <HeaderTitle>
                        Component
                    </HeaderTitle>
                </HeaderTitleContainer>
                <HeaderActionsContainer>
                    <HeaderAction title='Close' onClick={onClose}>
                        <CloseSvg />
                    </HeaderAction>
                </HeaderActionsContainer>
            </ModalHeader>
            <ModalBody>
                <div className='payload__configuration__container__body'>
                    {(Interfaces?.includes('GPIO') && selectedPayload) &&
                        <div className='hwil__configuration__container__body__section'>
                            <span className='body__section__header__text'>GPIO</span>
                            <div className='configuration__body__container__section'>
                                <div className='gpio__configuration__form__container'>
                                    <div className='configuration__form__container'>
                                        <span className='configuration__form__label'>Port</span>
                                    </div>
                                    <div className='configuration__form__container flex-row'>
                                        {[1, 2, 3, 4].map(pin => {
                                            return (
                                                <span key={pin} className='configuration__form__label'>Pin {pin}</span>
                                            )
                                        })}
                                    </div>
                                    <div className='configuration__form__container'>
                                        <span className='configuration__form__label'>Int</span>
                                    </div>
                                </div>
                                {[0, 1, 2, 3].map(port => {
                                    return (
                                        <div className='gpio__configuration__form__container'>
                                            <div className='configuration__form__container'>
                                                <input className='configuration__form__input' placeholder='#' disabled value={port + 1} />
                                            </div>
                                            <div className='configuration__form__container flex-row'>
                                                {[0, 1, 2, 3].map(pin => {
                                                    return (
                                                        <input className='configuration__form__input' placeholder='#'
                                                            type='number'
                                                            onKeyDown={validateNumberWithoutDecimal}
                                                            value={selectedPayload.gpio?.[port]?.['pin']?.[pin] ?? ''}
                                                            onChange={(e) => {
                                                                const value = e.target.value;
                                                                onPinChange(port, pin, value)
                                                            }}
                                                        />
                                                    )
                                                })}
                                            </div>
                                            <div className='configuration__form__container'>
                                                <input className='configuration__form__input' placeholder='#'
                                                    type='number'
                                                    onKeyDown={validateNumberWithoutDecimal}
                                                    value={selectedPayload.gpio?.[port]?.['interrupt'] || ''}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        onPortChange(port, value)
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    }
                    {(Interfaces?.includes('GPIO') && selectedPayload) && <div className='body__section__devider' />}
                    {(Interfaces?.includes('CAN') && selectedPayload) &&
                        <div className='hwil__configuration__container__body__section'>
                            <span className='body__section__header__text'>Device Path</span>
                            <div className='configuration__body__container__section'>
                                {[0, 1].map(can => {
                                    const required = can === 0 ? true : false
                                    const key = `canPath${can}`
                                    return (
                                        <div className='configuration__form__container'>
                                            <span className='configuration__form__label'>CAN 0{can + 1} {required ? '*' : ''}</span>
                                            <input required={required} className='configuration__form__input' placeholder='#'
                                                type='string'
                                                onKeyDown={(e) => { preventKeyInputs(e, ['+', '-', '.']) }}
                                                value={selectedPayload[key] || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, ' ').trimStart();
                                                    if (value.length > 8) return;
                                                    const newConfigurations = { ...selectedPayload }
                                                    newConfigurations[key] = value
                                                    onFormChange(newConfigurations)
                                                }}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    }
                    {(Interfaces?.includes('UART') && selectedPayload) &&
                        <div className='hwil__configuration__container__body__section'>
                            <div className='configuration__body__container__section'>
                                {[0, 1].map(uart => {
                                    const key = `uartPath${uart}`
                                    return (
                                        <div className='configuration__form__container'>
                                            <span className='configuration__form__label'>UART 0{uart + 1}</span>
                                            <input className='configuration__form__input' placeholder='#'
                                                type='string'
                                                onKeyDown={(e) => { preventKeyInputs(e, ['+']) }}
                                                value={selectedPayload[key] || ''}
                                                onChange={(e) => {
                                                    const value = e.target.value.replace(/[^a-zA-Z0-9\/\- ]/g, '').replace(/\s+/g, ' ').trimStart();
                                                    if (value.length > 8) return;
                                                    const newConfigurations = { ...selectedPayload }
                                                    newConfigurations[key] = value
                                                    onFormChange(newConfigurations)
                                                }}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    }
                    {(Interfaces?.includes('Ethernet') && selectedPayload) &&
                        <div className='hwil__configuration__container__body__section'>
                            <div className='configuration__body__container__section'>
                                <div className='configuration__form__container'>
                                    <span className='configuration__form__label'>ETHERNET *</span>
                                    <input className='configuration__form__input' placeholder='0.0.0.0'
                                        type='string'
                                        onKeyDown={onKeyDown}
                                        value={selectedPayload.payloadDeviceIP || ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (!/^[0-9.]*$/.test(value)) return
                                            const parts = value.split('.');
                                            const validIPAddress = (parts.length <= 4) && (parts.every(part => part.length <= 3 && Number(part) >= 0 && Number(part) <= 255));
                                            if (validIPAddress) {
                                                const newConfigurations = { ...selectedPayload }
                                                newConfigurations.payloadDeviceIP = e.target.value
                                                onFormChange(newConfigurations)
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </ModalBody>
            <ModalFooter>
                <FooterAction variant='outline__button'
                    onClick={onClose}
                >
                    Cancel
                </FooterAction>
                <FooterAction variant='primary__button'
                    disabled={disabled}
                    onClick={onSave}
                >
                    Save
                </FooterAction>
            </ModalFooter>
        </Modal>
    )
}

export default PayloadConfigModal